import { SideBarSectionAccordion } from "@core/components/SideBarSectionAccordion";
import { classNames } from "@core/lib/classNames";
import { getScreenType } from "@core/lib/ui/getScreenType";
import { StoryTileDrawerScreen } from "@core/screens/drawer/StoryTileDrawerScreen";
import { useUIState } from "@core/ui/UIState";
import { stAnalytics } from "@repo/analytics";
import type { curator } from "@repo/client";
import { type Component, For, createMemo } from "solid-js";
import styles from "./TextUnitStoryTiles.module.css";

export const TextUnitStoryTiles: Component<{
  tiles: Array<curator.Citation & { displayNumber: number }>;
  assetReferences?: curator.AssetRefMap;
}> = (props) => {
  const ctx = useUIState();
  const [, setRightContents] = ctx.rightDrawerContents;
  const [rightOpen, setRightOpen] = ctx.rightDrawer;

  // The tiles prop is now pre-filtered and sorted by the parent component (TextUnitV1)
  // It includes only tiles that are in the markdown and are sorted by displayNumber.
  const tiles = () => props.tiles;

  const format = (text: string) => {
    const applyPrefix = /^[a-z]/.test(text);
    const applySuffix = !text.endsWith(".") && !text.endsWith("!");
    return `${applyPrefix ? "..." : ""} ${text}${applySuffix ? "..." : ""}`;
  };

  return (
    <SideBarSectionAccordion
      label="Citations"
      depth={0}
      initiallyOpen
      modifier="chat"
      class="no-fade"
      onClick={() => {
        stAnalytics.track("click_tracking", {
          cta: "expand_storytile",
          position: "chat",
          screen: getScreenType(),
        });
      }}
    >
      <div class="w-full max-w-full overflow-x-auto -mx-2 px-2 pb-2 flex gap-3 pt-2 items-stretch">
        <For each={tiles()}>
          {(tile) => {
            const sourceName = () => `Citation #${tile.displayNumber}`;
            // Attempt to get a shorter, more human-readable part of the citationId for display
            const displayableCitationIdPart = () => {
              const parts = tile.citationId.split("_");
              return parts.length > 1 ? parts.slice(-2).join("_") : tile.citationId;
            };

            return (
              <button
                data-story-tile-id={tile.citationId}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRightContents(() => () => (
                    <StoryTileDrawerScreen
                      tiles={tiles()}
                      initial={tile.citationId}
                      assetRefMap={props.assetReferences}
                    />
                  ));
                  setRightOpen(true);
                  stAnalytics.track("click_tracking", {
                    cta: "open_storytile",
                    position: "chat",
                    screen: getScreenType(),
                    value: JSON.stringify({
                      citation_id: tile.citationId,
                      asset_reference_id: tile.assetReferenceId,
                    }),
                  });
                }}
                type="button"
                class={classNames(styles["story-tile"], "flex flex-col items-start text-left")}
              >
                <div class="text-xs font-semibold mb-1 w-full truncate" title={sourceName()}>
                  {sourceName()}
                </div>
                <span class={classNames(styles["story-tile__label"], "text-sm")}>{format(tile.text)}</span>
              </button>
            );
          }}
        </For>
      </div>
    </SideBarSectionAccordion>
  );
};
